import { useAppDispatch } from 'legacy/lut/hooks/useAppDispatch'
import { useCallback, useEffect, useState } from 'react'

import { OnboardingResponseStatus } from 'clickNgo/configs/service/onboarding'
import { updatePartner } from 'clickNgo/entities/partner/api'
import { IPartner } from 'clickNgo/entities/partner/models'
import { IPartnerFormLegal } from 'clickNgo/entities/partner/models/form/legal'
import { getJSendErrorMessages } from 'clickNgo/helpers/api'
import { resetModifiedDataByField, useGetFormDataField } from 'cng-common/features/form'
import { TForm } from 'cng-common/features/form/models'
import Logger from 'core/utils/Logger'

import {
  PartnerEditorFormKeys,
  PartnerEditorFormSettings,
  UpdatePartnerResult,
} from '../../../../configs'
import { IPartnerEditorData } from '../../../../models'

interface IUseEditPartnerLegalInfoHandlerParams {
  partner: IPartner
}

export const useEditPartnerLegalInfoHandler = ({
  partner,
}: IUseEditPartnerLegalInfoHandlerParams) => {
  const dispatch = useAppDispatch()
  const [errorMessages, setErrorMessages] = useState<string[]>([])
  const [actualData, updateActualData] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.ActualData
  )
  const [currentMenuItem] = useGetFormDataField<IPartnerEditorData>(
    PartnerEditorFormKeys.Setting,
    PartnerEditorFormSettings.CurrentMenuItem
  )

  const handleOnPartnerLegalChangesApply = useCallback(
    async ({ modifiedFields, formData, invalidFields }: TForm<IPartnerFormLegal>) => {
      setErrorMessages([])
      const hasInvalidModifiedFields = Object.keys(invalidFields).some((invalidField) =>
        modifiedFields.includes(invalidField)
      )

      if (hasInvalidModifiedFields) {
        Logger.error('HAS INVALID FIELDS')
        return UpdatePartnerResult.Failed
      }

      const updatedPartnerData: IPartner = {
        ...partner,
        ...formData,
        legalModifiedValues: modifiedFields,
      }

      const result = await updatePartner(updatedPartnerData)

      if (result.status === OnboardingResponseStatus.Success) {
        updateActualData({
          ...actualData,
          partner: {
            ...updatedPartnerData,
            entryId502: result.data.entryId502 || updatedPartnerData.entryId502,
            legalWorkflowStatus: result.data.entryId502
              ? result.data.legalWorkflowStatus
              : updatedPartnerData.legalWorkflowStatus,
            legalModifiedValues: result.data.entryId502
              ? result.data.legalModifiedValues
              : updatedPartnerData.legalModifiedValues,
          },
        })
        return result.data.entryId502 ? UpdatePartnerResult.Requested : UpdatePartnerResult.Updated
      }

      if (result.status === OnboardingResponseStatus.Fail) {
        setErrorMessages(getJSendErrorMessages(result))
        return UpdatePartnerResult.Failed
      }

      if (result.status === OnboardingResponseStatus.Error) {
        setErrorMessages([result.message])
        return UpdatePartnerResult.Failed
      }

      setErrorMessages(['Unknown error'])
      return UpdatePartnerResult.Failed
    },
    [actualData, partner, updateActualData]
  )

  const handleOnPartnerLegalChangesCancel = useCallback(
    ({ modifiedFields }: TForm<IPartnerFormLegal>) => {
      modifiedFields.forEach((field) => {
        dispatch(resetModifiedDataByField({ formKey: PartnerEditorFormKeys.LegalInfo, field }))
      })
    },
    [dispatch]
  )

  useEffect(() => {
    return () => {
      setErrorMessages([])
    }
  }, [currentMenuItem])

  return { handleOnPartnerLegalChangesApply, handleOnPartnerLegalChangesCancel, errorMessages }
}
